<template>
  <div>
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Income Statement Report</h5>
        <p class="page-sub">Revenue and expenses over a selected period</p>
      </div>
      <div class="page-actions">

        <!-- Date Range — exact original filters -->
        <div class="date-range">
          <div class="date-field">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.start_date"
              :enableTimePicker="false"
              :hideInputIcon="true"
              class="date-picker"
              placeholder="Start date"
            />
          </div>
          <span class="date-sep">—</span>
          <div class="date-field">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.end_date"
              :enableTimePicker="false"
              :hideInputIcon="true"
              class="date-picker"
              placeholder="End date"
            />
          </div>
        </div>

        <button class="btn-primary-action" @click="fetchData()" :disabled="crudLoading">
          <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <font-awesome-icon v-else icon="search" class="me-1" />
          {{ crudLoading ? 'Loading...' : 'Search' }}
        </button>

        <div class="dropdown">
          <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" href="#" @click.prevent="exportPDF('income_statement_report')">
              <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
            </a></li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="report-wrap">
        <div class="skeleton skeleton--letterhead mb-4"></div>
        <div class="skeleton skeleton--sechdr mb-2"></div>
        <div class="skeleton skeleton--row mb-2" v-for="n in 4" :key="'i'+n"></div>
        <div class="skeleton skeleton--subtotal mb-4 mt-2"></div>
        <div class="skeleton skeleton--sechdr mb-2"></div>
        <div class="skeleton skeleton--row mb-2" v-for="n in 3" :key="'e'+n"></div>
        <div class="skeleton skeleton--subtotal mt-2 mb-4"></div>
        <div class="skeleton skeleton--total mt-2"></div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="!hasData" class="data-card">
      <div class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="chart-line" />
        </div>
        <p class="state-title">No report data</p>
        <p class="state-sub">Select a date range and click Search to generate the income statement.</p>
      </div>
    </div>

    <!-- Report -->
    <div v-else class="report-wrap" ref="pdfContent">

      <!-- Letterhead -->
      <div class="report-letterhead">
        <div class="letterhead-left">
          <div class="letterhead-icon">
            <font-awesome-icon icon="chart-line" />
          </div>
          <div>
            <h2 class="letterhead-title">Income Statement</h2>
            <p class="letterhead-sub">
              {{ formatDate(tableData.start_date) }} — {{ formatDate(tableData.end_date) }}
            </p>
          </div>
        </div>
        <div class="letterhead-right">
          <!-- Net Profit chip -->
          <div class="profit-chip" :class="netProfit >= 0 ? 'profit-chip--profit' : 'profit-chip--loss'">
            <font-awesome-icon :icon="netProfit >= 0 ? 'arrow-up' : 'arrow-down'" class="me-1" />
            {{ netProfit >= 0 ? 'Net Profit' : 'Net Loss' }}
            <span class="profit-chip-amount">{{ formatPrice(Math.abs(netProfit)) }}</span>
          </div>
          <!-- Summary bar -->
          <div class="totals-bar">
            <div class="totals-bar-item">
              <span class="totals-bar-label">Total Income</span>
              <span class="totals-bar-value totals-bar-value--green">{{ formatPrice(totalIncome) }}</span>
            </div>
            <span class="totals-bar-sep">−</span>
            <div class="totals-bar-item">
              <span class="totals-bar-label">Total Expenses</span>
              <span class="totals-bar-value totals-bar-value--red">{{ formatPrice(totalExpenses) }}</span>
            </div>
            <span class="totals-bar-sep">=</span>
            <div class="totals-bar-item">
              <span class="totals-bar-label">Net</span>
              <span class="totals-bar-value" :class="netProfit >= 0 ? 'totals-bar-value--green' : 'totals-bar-value--red'">
                {{ formatPrice(netProfit) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Income Section -->
      <div class="report-section mb-4">
        <div class="sec-hdr sec-hdr--green">
          <font-awesome-icon icon="arrow-up" />
          <span>Income</span>
          <span class="sec-hdr-total">{{ formatPrice(totalIncome) }}</span>
        </div>
        <table class="rep-table">
          <thead>
            <tr>
              <th>Account</th>
              <th class="tr" style="width:200px">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in incomeList" :key="index">
              <td>
                <div class="acc-cell">
                  <span class="acc-code">{{ row.code }}</span>
                  <span class="acc-name">{{ row.name }}</span>
                </div>
              </td>
              <td class="tr fw6 income-value">{{ formatPrice(row.amount) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="total-label">Total Income</td>
              <td class="tr total-amount total-amount--green">{{ formatPrice(totalIncome) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Expenses Section -->
      <div class="report-section mb-4">
        <div class="sec-hdr sec-hdr--red">
          <font-awesome-icon icon="arrow-down" />
          <span>Expenses</span>
          <span class="sec-hdr-total">({{ formatPrice(totalExpenses) }})</span>
        </div>
        <table class="rep-table">
          <thead>
            <tr>
              <th>Account</th>
              <th class="tr" style="width:200px">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in expenseList" :key="index">
              <td>
                <div class="acc-cell">
                  <span class="acc-code">{{ row.code }}</span>
                  <span class="acc-name">{{ row.name }}</span>
                </div>
              </td>
              <td class="tr fw6 expense-value">({{ formatPrice(row.amount) }})</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="total-label">Total Expenses</td>
              <td class="tr total-amount total-amount--red">({{ formatPrice(totalExpenses) }})</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Net Profit / Loss -->
      <div class="net-row" :class="netProfit >= 0 ? 'net-row--profit' : 'net-row--loss'">
        <div class="net-row-left">
          <div class="net-row-icon">
            <font-awesome-icon :icon="netProfit >= 0 ? 'check-circle' : 'exclamation-circle'" />
          </div>
          <div>
            <p class="net-row-label">{{ netProfit >= 0 ? 'Net Profit' : 'Net Loss' }}</p>
            <p class="net-row-sub">Income minus Expenses for the period</p>
          </div>
        </div>
        <span class="net-row-value">{{ formatPrice(netProfit) }}</span>
      </div>

      <div class="rep-footer">Generated by Mapiibs Solution</div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../../lib/reusableMixin';
import { useStore } from 'vuex';
import ReportHeaderTab from './components/reportheadertab.vue';
import moment from 'moment';

export default {
  components: { ReportHeaderTab },
  mixins: [reusableMixin],

  data() {
    return {
      income:        [],
      expenses:      [],
      totalIncome:   0,
      totalExpenses: 0,
      netProfit:     0,
      loading:       false,
      crudLoading:   false,
      query:         null,

      tableData: { page: 1,
        start_date: new Date(new Date().getFullYear(), 0, 1), // Jan 1 of current year
        end_date:   new Date(),
        draw: 0, length: 10, search: '', column: 0, dir: 'desc', id: '',
      },

      toast: useToast(),
      store: useStore(),
    };
  },

  computed: {
    permission()  { return this.store.state.getCurrentUserPermission; },
    hasData()     { return this.incomeList.length > 0 || this.expenseList.length > 0; },
    incomeList()  { return Array.isArray(this.income)   ? this.income   : Object.values(this.income   ?? {}); },
    expenseList() { return Array.isArray(this.expenses) ? this.expenses : Object.values(this.expenses ?? {}); },
  },

  watch: { query() { this.fetchData(); } },
  created() { this.fetchData(); },

  methods: {
    // Exact original fetchData logic
    fetchData(url = '/accounting/report/income-statement') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;
      if (this.crudLoading && this.query) { this.crudLoading = false; }
      this.tableData.draw++;
      axios.get(url, { params: this.tableData })
        .then(response => {
          this.loading = false; this.crudLoading = false;
          if (!response.data.status == 200) return;
          const d          = response.data.data;
          this.income        = d.income;
          this.expenses      = d.expenses;
          this.totalIncome   = d.totalIncome   ?? 0;
          this.totalExpenses = d.totalExpenses ?? 0;
          this.netProfit     = d.netProfit     ?? 0;
        })
        .catch(error => {
          this.loading = false; this.crudLoading = false;
          if ([400, 401, 403, 500].includes(error.response?.status)) {
            this.toast.error(error.response.data.message);
          }
        });
    },

    formatDate(date) { return moment(date).format('DD/MM/YYYY'); },

    formatPrice(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* ── Date Range ── */
.date-range { display:flex; align-items:center; gap:6px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:4px 10px; }
.date-field { position:relative; display:flex; align-items:center; }
.date-icon  { position:absolute; left:8px; color:#7a90b0; font-size:12px; z-index:2; pointer-events:none; }
.date-sep   { color:#aab8cc; font-size:13px; flex-shrink:0; }
:deep(.date-picker)       { border:none !important; background:transparent !important; box-shadow:none !important; }
:deep(.date-picker input) { border:none !important; font-size:13px; color:#1a2b45; padding:4px 4px 4px 24px !important; width:130px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline::after { display:none !important; }
.me-1 { margin-right:4px; } .me-2 { margin-right:8px; }

/* ── Skeleton ── */
.skeleton     { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--letterhead { height:90px; }
.skeleton--sechdr     { height:42px; border-radius:10px; }
.skeleton--row        { height:18px; }
.skeleton--subtotal   { height:22px; width:40%; margin-left:auto; }
.skeleton--total      { height:56px; border-radius:12px; }
.mb-2 { margin-bottom:8px; } .mb-4 { margin-bottom:16px; } .mt-2 { margin-top:8px; }

/* ── Empty ── */
.data-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; }
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }

/* ── Report Wrap ── */
.report-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:32px; }

/* ── Letterhead ── */
.report-letterhead { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; padding-bottom:24px; border-bottom:2px solid #e4eaf2; margin-bottom:24px; flex-wrap:wrap; }
.letterhead-left   { display:flex; align-items:center; gap:16px; }
.letterhead-icon   { width:52px; height:52px; border-radius:14px; background:rgba(22,163,74,.1); color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
.letterhead-title  { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 3px; }
.letterhead-sub    { font-size:13px; color:#7a90b0; margin:0; }
.letterhead-right  { display:flex; flex-direction:column; align-items:flex-end; gap:10px; }

/* ── Profit Chip ── */
.profit-chip          { display:inline-flex; align-items:center; gap:8px; border-radius:8px; padding:7px 14px; font-size:12.5px; font-weight:700; border:1px solid transparent; }
.profit-chip--profit  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.profit-chip--loss    { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
.profit-chip-amount   { font-size:16px; font-weight:800; margin-left:4px; }

/* ── Totals Bar ── */
.totals-bar              { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:10px 16px; flex-wrap:wrap; }
.totals-bar-item         { display:flex; flex-direction:column; gap:2px; }
.totals-bar-label        { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.totals-bar-value        { font-size:16px; font-weight:800; color:#1a2b45; }
.totals-bar-value--green { color:#16a34a; }
.totals-bar-value--red   { color:#e03535; }
.totals-bar-sep          { font-size:16px; font-weight:700; color:#aab8cc; }

/* ── Sections ── */
.report-section     { border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.report-section.mb-4 { margin-bottom:16px; }
.sec-hdr        { display:flex; align-items:center; gap:10px; padding:12px 16px; font-size:14px; font-weight:700; }
.sec-hdr--green  { background:rgba(22,163,74,.07); color:#16a34a; }
.sec-hdr--red    { background:rgba(224,53,53,.07);  color:#e03535; }
.sec-hdr-total   { margin-left:auto; font-size:13px; font-weight:600; opacity:.85; }

/* ── Table ── */
.rep-table { width:100%; border-collapse:collapse; }
.rep-table thead th        { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 14px; border-bottom:1px solid #e4eaf2; background:#f8fafc; }
.rep-table tbody tr        { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.rep-table tbody tr:last-child { border-bottom:none; }
.rep-table tbody tr:hover  { background:#fafbfc; }
.rep-table tbody td        { padding:10px 14px; font-size:13.5px; }
.rep-table tfoot tr        { background:#f8fafc; }
.rep-table tfoot td        { padding:11px 14px; font-size:13.5px; border-top:2px solid #e4eaf2; }
.tr  { text-align:right; }
.fw6 { font-weight:700; }

.acc-cell    { display:flex; align-items:center; gap:9px; }
.acc-code    { font-family:monospace; font-size:11px; font-weight:600; color:#7a90b0; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:5px; padding:1px 6px; flex-shrink:0; }
.acc-name    { font-size:13.5px; color:#1a2b45; }

.income-value  { color:#16a34a; }
.expense-value { color:#e03535; }

.total-label          { font-size:13px; font-weight:700; color:#1a2b45; }
.total-amount         { font-size:15px; font-weight:800; }
.total-amount--green  { color:#16a34a; }
.total-amount--red    { color:#e03535; }

/* ── Net Profit/Loss Row ── */
.net-row          { display:flex; align-items:center; justify-content:space-between; border-radius:12px; padding:18px 20px; border:1px solid transparent; }
.net-row--profit  { background:rgba(22,163,74,.07); border-color:#bbf7d0; }
.net-row--loss    { background:rgba(224,53,53,.07);  border-color:#fac5c5; }
.net-row-left     { display:flex; align-items:center; gap:14px; }
.net-row-icon     { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.net-row--profit .net-row-icon { background:rgba(22,163,74,.15);  color:#16a34a; }
.net-row--loss   .net-row-icon { background:rgba(224,53,53,.15); color:#e03535; }
.net-row-label    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.net-row-sub      { font-size:12px; color:#7a90b0; margin:0; }
.net-row-value    { font-size:26px; font-weight:800; }
.net-row--profit .net-row-value { color:#16a34a; }
.net-row--loss   .net-row-value { color:#e03535; }

/* ── Footer ── */
.rep-footer { text-align:center; margin-top:28px; padding-top:20px; border-top:1px solid #e4eaf2; font-size:12px; color:#aab8cc; }

/* ── Dropdown ── */
.dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
.dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
.dropdown-item:hover { background:#f4f7fb; }

/* ── Print ── */
@media print {
  .page-header,.btn-primary-action,.btn-outline,.dropdown { display:none !important; }
  .report-wrap { border:none; padding:0; }
}

/* ── Responsive ── */
@media(max-width:767px) {
  .page-header,.page-actions { flex-direction:column; align-items:stretch; }
  .report-letterhead { flex-direction:column; }
  .letterhead-right  { align-items:flex-start; }
  .report-wrap { padding:16px; }
  .totals-bar  { flex-wrap:wrap; }
  .net-row     { flex-direction:column; align-items:flex-start; gap:12px; }
}
</style>